import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./PlatformSubscriptionsPage.css";
import { useOrganizations } from "../../../hooks/useOrganizations";
import { useOrgEntitlementsMap } from "../../../hooks/useOrgEntitlementsMap";
import SubscriptionStatusBadge from "../../../components/SubscriptionStatusBadge";
import { formatDate } from "../../../utils/DateTime";

const PlatformSubscriptionsPage = () => {
  const navigate = useNavigate();
  const {
    organizations,
    pagination,
    loading,
    error,
    setPage,
    reload,
  } = useOrganizations();

  const orgIds = useMemo(
    () => organizations.map((org) => org.id),
    [organizations],
  );
  const {
    map: entitlementsMap,
    loading: subsLoading,
    error: subsError,
  } = useOrgEntitlementsMap(orgIds);

  const startPage = (pagination.page - 1) * pagination.pageSize + 1;
  const endPage = Math.min(
    pagination.page * pagination.pageSize,
    pagination.totalCount,
  );

  const openSubscription = (orgId: string, action?: string) => {
    const query = action ? `?action=${action}` : "";
    navigate(`/organizations/${orgId}/subscription${query}`);
  };

  const renderBody = () => {
    if (loading) {
      return <div className="subs-empty">Loading organizations…</div>;
    }

    if (error) {
      return (
        <div className="subs-empty">
          <p>{error}</p>
          <button className="subs-btn subs-btn--secondary" onClick={reload}>
            Retry
          </button>
        </div>
      );
    }

    if (organizations.length === 0) {
      return (
        <div className="subs-empty">
          No organizations yet. Create one from the Organizations page.
        </div>
      );
    }

    return (
      <div className="subs-table-wrap">
        <table className="subs-table">
          <thead>
            <tr>
              <th>Organization</th>
              <th>Plan</th>
              <th>Status</th>
              <th>Valid until</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {organizations.map((org) => {
              const ent = entitlementsMap[org.id];
              const hasActive = ent?.hasActiveSubscription === true;
              return (
                <tr key={org.id}>
                  <td>
                    <div className="subs-org">
                      <span className="subs-avatar">
                        {org.name.charAt(0).toUpperCase()}
                      </span>
                      {org.name}
                    </div>
                  </td>
                  <td>
                    {subsLoading && !ent ? (
                      <span className="subs-muted">Loading…</span>
                    ) : ent?.planName ? (
                      <>
                        {ent.planName}
                        {ent.planCode ? (
                          <span className="subs-code"> /{ent.planCode}</span>
                        ) : null}
                      </>
                    ) : (
                      <span className="subs-muted">No plan</span>
                    )}
                  </td>
                  <td>
                    {ent ? (
                      <SubscriptionStatusBadge status={ent.status} />
                    ) : (
                      <span className="subs-muted">—</span>
                    )}
                  </td>
                  <td>
                    {subsLoading && !ent ? (
                      <span className="subs-muted">…</span>
                    ) : ent?.endsAt ? (
                      formatDate(ent.endsAt)
                    ) : (
                      <span className="subs-muted">—</span>
                    )}
                  </td>
                  <td className="subs-actions">
                    {hasActive ? (
                      <>
                        <button
                          className="subs-btn subs-btn--small subs-btn--secondary"
                          onClick={() => openSubscription(org.id, "extend")}
                          title="Renew this organization's subscription on the same plan"
                        >
                          Extend
                        </button>
                        <button
                          className="subs-btn subs-btn--small subs-btn--secondary"
                          onClick={() => openSubscription(org.id, "change")}
                          title="Upgrade or downgrade this organization's plan"
                        >
                          Change plan
                        </button>
                      </>
                    ) : (
                      <button
                        className="subs-btn subs-btn--small subs-btn--primary"
                        onClick={() => openSubscription(org.id, "activate")}
                        title="Activate a subscription for this organization"
                      >
                        Activate
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="subs-page">
      <div className="subs-header">
        <div>
          <h2>Subscriptions / Billing</h2>
          <p>
            Activate and extend paid plans after manual payment
            (bank/bKash).
          </p>
        </div>
      </div>

      {subsError && (
        <div className="subs-error">
          {subsError} — showing cached data where available.
        </div>
      )}

      {renderBody()}

      {!loading && !error && organizations.length > 0 && (
        <div className="subs-pagination">
          <span className="subs-pagination__info">
            Showing {startPage}–{endPage} of {pagination.totalCount}
          </span>
          <div className="subs-pagination__controls">
            <button
              className="subs-btn subs-btn--secondary"
              disabled={!pagination.hasPrevious}
              onClick={() => setPage(pagination.page - 1)}
            >
              Previous
            </button>
            <span className="subs-pagination__page">
              Page {pagination.page} of {Math.max(1, pagination.totalPages)}
            </span>
            <button
              className="subs-btn subs-btn--secondary"
              disabled={!pagination.hasNext}
              onClick={() => setPage(pagination.page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformSubscriptionsPage;